/**
 * App Job Type Definitions
 *
 * TypeScript types for app job entities and operations.
 * App jobs define cron-based recurring execution of application functions
 * using service accounts for execution identity.
 */

// ============================================================================
// APP JOB STATUS ENUM
// ============================================================================

export enum AppJobStatus {
  RUNNING = "running",
  PAUSED = "paused",
  DISABLED = "disabled",
}

// ============================================================================
// APP JOB ENTITY
// ============================================================================

/**
 * AppJob - Recurring function execution configuration
 */
export interface AppJob {
  entityType: "APP_JOB";
  tenantId: string;
  appJobId: string; // UUID v4
  appJobCode: string; // Unique code (immutable after creation)

  // Application binding
  applicationId: string;
  applicationCode: string; // Denormalized for display

  // Service account for execution identity
  serviceAccountId: string;
  serviceAccountCode: string; // Denormalized for display
  serviceAccountName: string; // Denormalized (firstName + lastName)

  // Identity
  appJobName: string; // Display name
  description?: string;

  // Function to execute
  functionName: string;
  payload?: Record<string, unknown>; // Payload to pass to function

  // Scheduling
  cronExpression: string; // Standard cron format (minute hour day month weekday)
  timezone: string; // IANA timezone (e.g., "America/New_York")

  // Status
  status: AppJobStatus;
  isActive: boolean; // Convenience flag (status === RUNNING)

  // Next/Last execution tracking
  nextRunAt?: string; // ISO 8601 UTC timestamp
  lastRunAt?: string; // ISO 8601 UTC timestamp
  lastRunStatus?: "completed" | "failed";
  lastRunJobId?: string; // Reference to background job

  // Audit fields
  createdBy: string; // User ID who created the app job
  updatedBy?: string; // User ID who last updated

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

/**
 * Sanitized app job (for API responses)
 */
export type AppJobSanitized = AppJob;

// ============================================================================
// DTOs
// ============================================================================

/**
 * DTO for creating an app job
 */
export interface AppJobCreateDTO {
  appJobCode: string; // Unique code (immutable after creation)
  appJobName: string; // Display name
  description?: string;
  serviceAccountId: string; // Service account for execution identity
  functionName: string;
  payload?: Record<string, unknown>;
  cronExpression: string;
  timezone: string;
}

/**
 * DTO for updating an app job
 */
export interface AppJobUpdateDTO {
  description?: string;
  functionName?: string;
  payload?: Record<string, unknown>;
  cronExpression?: string;
  timezone?: string;
}

// ============================================================================
// QUERY TYPES
// ============================================================================

/**
 * App job list query parameters
 */
export interface AppJobListQuery {
  applicationId?: string;
  limit?: number;
  cursor?: string;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

/**
 * App job list response
 */
export interface AppJobListResponse {
  appJobs: AppJobSanitized[];
  count: number;
  lastEvaluatedKey?: Record<string, unknown>;
}

/**
 * App job execution history entry (for app job history modal)
 */
export interface AppEltJobExecution {
  jobId: string;
  status: "pending" | "running" | "completed" | "failed";
  startedAt: string;
  completedAt: string | null;
  error?: { code: string; message: string };
}
